import React from "react";
import { Link } from "react-router-dom";
import { Logo } from "./Logo";
import { useAuth } from "../context/AuthContext";

export default function Footer() {
    const { user } = useAuth();
    const isAppMode = !!user && (user.is_paid || user.role === "admin");

    // App-mode: a paid user is "in the app" — show a minimal footer with no
    // marketing/upsell links, just the legal essentials.
    if (isAppMode) {
        return (
            <footer className="border-t border-black/10 bg-white mt-auto" data-testid="footer">
                <div className="mx-auto max-w-7xl px-4 md:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-ink-muted">
                    <span>© {new Date().getFullYear()} OpenCSV · opencsv.io</span>
                    <div className="flex items-center gap-4">
                        <Link to="/account" className="hover:text-cobalt" data-testid="footer-account">Account</Link>
                        <Link to="/terms" className="hover:text-cobalt" data-testid="footer-terms">Terms</Link>
                        <Link to="/privacy" className="hover:text-cobalt" data-testid="footer-privacy">Privacy</Link>
                    </div>
                </div>
            </footer>
        );
    }

    // Default: full marketing footer for visitors / unpaid users.
    return (
        <footer className="border-t border-black/10 bg-white" data-testid="footer">
            <div className="mx-auto max-w-7xl px-4 md:px-8 py-12 grid grid-cols-2 md:grid-cols-6 gap-8">
                <div className="col-span-2">
                    <Logo />
                    <p className="mt-4 text-sm text-ink-soft max-w-sm">
                        The fastest CSV viewer & editor on the web. One dollar. Lifetime access. No bloat.
                    </p>
                    <p className="mt-4 text-xs text-ink-muted">Built by <Link to="/about" className="hover:text-cobalt underline">Flying Fitz</Link> in St. John's, Newfoundland.</p>
                </div>

                <div>
                    <div className="ui-heading text-xs uppercase tracking-widest text-ink-muted mb-3">Product</div>
                    <ul className="space-y-2 text-sm">
                        <li><Link to="/csv-viewer" className="hover:text-cobalt" data-testid="footer-viewer">CSV Viewer</Link></li>
                        <li><Link to="/csv-editor" className="hover:text-cobalt" data-testid="footer-editor">CSV Editor</Link></li>
                        <li><Link to="/features" className="hover:text-cobalt" data-testid="footer-features">All features</Link></li>
                        <li><Link to="/pricing" className="hover:text-cobalt" data-testid="footer-pricing">Pricing</Link></li>
                        <li><Link to="/pricing" className="hover:text-cobalt" data-testid="footer-pricing">See pricing</Link></li>
                    </ul>
                </div>

                <div>
                    <div className="ui-heading text-xs uppercase tracking-widest text-ink-muted mb-3">Learn</div>
                    <ul className="space-y-2 text-sm">
                        <li><Link to="/what-is-a-csv-file" className="hover:text-cobalt" data-testid="footer-what">What is a CSV file?</Link></li>
                        <li><Link to="/how-to-edit-csv-files" className="hover:text-cobalt" data-testid="footer-howto">How to edit CSV</Link></li>
                        <li><Link to="/open-csv-files" className="hover:text-cobalt" data-testid="footer-open">Open CSV files</Link></li>
                        <li><Link to="/csv-vs-excel" className="hover:text-cobalt" data-testid="footer-vs">CSV vs Excel</Link></li>
                        <li><Link to="/csv-use-cases" className="hover:text-cobalt" data-testid="footer-uses">Use cases</Link></li>
                        <li><Link to="/import-export-csv" className="hover:text-cobalt" data-testid="footer-impexp">Import & export</Link></li>
                    </ul>
                </div>

                <div>
                    <div className="ui-heading text-xs uppercase tracking-widest text-ink-muted mb-3">Resources</div>
                    <ul className="space-y-2 text-sm">
                        <li><Link to="/blog" className="hover:text-cobalt" data-testid="footer-blog">Blog</Link></li>
                        <li><Link to="/ask" className="hover:text-cobalt" data-testid="footer-ask">Ask: CSV answers</Link></li>
                        <li><Link to="/faq" className="hover:text-cobalt" data-testid="footer-faq">FAQ</Link></li>
                        <li><Link to="/fast-csv-viewer" className="hover:text-cobalt" data-testid="footer-fast">Fast CSV viewer</Link></li>
                        <li><Link to="/lightweight-csv-editor" className="hover:text-cobalt" data-testid="footer-light">Lightweight editor</Link></li>
                        <li><Link to="/privacy-focused-csv-viewer" className="hover:text-cobalt" data-testid="footer-priv-feature">Privacy-focused</Link></li>
                    </ul>
                </div>

                <div>
                    <div className="ui-heading text-xs uppercase tracking-widest text-ink-muted mb-3">Company</div>
                    <ul className="space-y-2 text-sm">
                        <li><Link to="/about" className="hover:text-cobalt" data-testid="footer-about">About</Link></li>
                        <li><Link to="/terms" className="hover:text-cobalt" data-testid="footer-terms">Terms</Link></li>
                        <li><Link to="/privacy" className="hover:text-cobalt" data-testid="footer-privacy">Privacy</Link></li>
                    </ul>
                </div>
            </div>
            <div className="border-t border-black/10 px-4 md:px-8 py-4 mx-auto max-w-7xl flex flex-col md:flex-row justify-between text-xs text-ink-muted">
                <span>© {new Date().getFullYear()} OpenCSV · opencsv.io</span>
                <span>Built for makers who hate slow tools.</span>
            </div>
        </footer>
    );
}
